import {
  format,
  eachDayOfInterval,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  getDay,
  addMonths,
  subMonths,
  lastDayOfMonth,
  setDate,
  isWithinInterval,
  isSameDay,
  startOfDay,
  endOfDay,
  isSameMonth,
  addDays,
  isToday,
  isBefore,
  parse,
} from 'date-fns';
import { frCA } from 'date-fns/locale';
import _ from 'lodash';

import Component from '../classes/component';
import { ComponentType } from '../types/app-types';
import { PickupSettings } from '../types/store-types';
import { disableIf } from '../utils/dom-utils';
import StatefulButton from './stateful-button';

const SELECTORS = {
  calendar: '.calendar',
  monthLabel: '#current-month',
  daysGrid: '.days-wrapper',
  selectedItem: '.days-wrapper .item.selected',
  timesWrapper: '.times-wrapper',
  selectedTimeOption: '.times-wrapper .time.selected',
  prevMonth: '#prev-month',
  nextMonth: '#next-month',
  dateLabel: '#date-label',
  timeLabel: '#time-label',
};

const HOURS_FORMAT = "H'h'mm";

class Calendar extends Component<typeof SELECTORS> {
  protected _selectors = SELECTORS;

  private _selectedDate = new Date();

  private _settings: PickupSettings;

  constructor(type: ComponentType, container: HTMLElement) {
    super(type, container);
    this._updateCalendar();
  }

  private _updateMonthLabel() {
    this._getChild('monthLabel').innerText = format(this._selectedDate, 'LLLL', { locale: frCA });

    if (!this._settings) return;

    disableIf(
      this._getChild('prevMonth'),
      isSameMonth(this._selectedDate, this._settings.interval.start),
    );
    disableIf(
      this._getChild('nextMonth'),
      isSameMonth(this._selectedDate, this._settings.interval.end),
    );
  }

  private _renderDaysOfWeek() {
    const now = new Date();
    const items = eachDayOfInterval({ start: startOfWeek(now), end: endOfWeek(now) }).map(day => {
      const item = document.createElement('div');
      item.className = 'item day-of-week';
      item.innerText = format(day, 'EEEEE', { locale: frCA });

      return item;
    });

    this._getChild('daysGrid').replaceChildren(...items);
  }

  private _dateFromTime(time: string) {
    return parse(time, HOURS_FORMAT, new Date());
  }

  private _formattedTime(date: Date | string) {
    const dateObj = _.isString(date) ? this._dateFromTime(date) : date;
    return format(dateObj, dateObj.getMinutes() ? HOURS_FORMAT : "H'h'");
  }

  private _renderToggleContent() {
    const date = format(this._selectedDate, 'd/L/yy');
    const [_period, time] = this._getChild('selectedTimeOption')?.innerHTML.split('</span>') ?? [
      '',
      '',
    ];
    this._getChild('dateLabel').innerText = date;
    this._getChild('timeLabel').innerText = time;
  }

  private _renderTimeIntervals() {
    if (!this._settings) return;

    const choices = [];

    const activeSchedule = this._settings.schedule[this._selectedDate.getDay()];
    if (activeSchedule) {
      const fromDate = this._dateFromTime(activeSchedule.from);
      const toDate = this._dateFromTime(activeSchedule.to);
      const amTresholdDate = this._dateFromTime(this._settings.am_treshold);

      if (isToday(this._selectedDate)) {
        if (this._settings.can_pickup_today_from && isBefore(Date.now(), toDate)) {
          choices.push(['PM', this._settings.can_pickup_today_from, activeSchedule.to]);
        }
      } else {
        if (isBefore(fromDate, amTresholdDate)) {
          choices.push(['AM', activeSchedule.from, this._settings.am_treshold]);
        }

        choices.push(['PM', this._settings.pm_treshold, activeSchedule.to]);
      }
    }

    this._getChild('timesWrapper').replaceChildren(
      ...choices.map(([period, from, to], index) => {
        const time = document.createElement('div');
        time.className = 'time';
        if (!index) time.classList.add('selected');

        const schedule = `${this._formattedTime(from)} - ${this._formattedTime(to)}`;
        time.innerHTML = `<span class="period">${period}</span>${schedule}`;

        return time;
      }),
    );
  }

  private _renderDaysOfMonth() {
    const items = eachDayOfInterval({
      start: startOfMonth(this._selectedDate),
      end: endOfMonth(this._selectedDate),
    });

    const offset = getDay(items[0]);
    _.range(offset).forEach(() => {
      const item = document.createElement('div');
      item.className = 'item day-of-month empty';

      this._getChild('daysGrid').appendChild(item);
    });

    const days = items.map(day => {
      const item = document.createElement('div');
      item.className = 'item day-of-month';

      if (isSameDay(day, this._selectedDate)) {
        item.classList.add('selected');
      }

      if (this._settings) {
        if (
          !isWithinInterval(day, this._settings.interval) ||
          !this._settings.schedule[day.getDay()]
        ) {
          item.classList.add('disabled');
        }
      }

      item.innerText = format(day, 'd', { locale: frCA });

      return item;
    });

    this._getChild('daysGrid').append(...days);

    const totalCellsCount = 6 * 7;
    const rest = totalCellsCount - offset - days.length;
    _.range(rest).forEach(() => {
      const item = document.createElement('div');
      item.className = 'item day-of-month empty';

      this._getChild('daysGrid').appendChild(item);
    });
  }

  private _verifySelection() {
    if (!this._settings) return;

    const activeDay = this._selectedDate.getDay();
    const activeSchedule = this._settings.schedule[activeDay];

    if (!activeSchedule) {
      for (let i = 0; i < 7; i++) {
        const day = (activeDay + i) % 7;
        if (this._settings.schedule[day]) {
          this._selectedDate = addDays(this._selectedDate, i);
          return;
        }
      }
    }
  }

  private _updateCalendar() {
    this._verifySelection();

    this._updateMonthLabel();
    this._renderDaysOfWeek();
    this._renderDaysOfMonth();
    this._renderTimeIntervals();
    this._renderToggleContent();
  }

  private _handleGridClick(event: Event) {
    const item = event.target as HTMLElement;
    if (
      item.classList.contains('day-of-month') &&
      !item.classList.contains('empty') &&
      !item.classList.contains('disabled')
    ) {
      this._getChildren('selectedItem').forEach(selectedItem => {
        selectedItem.classList.remove('selected');
      });

      const dayOfMonth = Number(item.innerText);

      if (_.isNumber(dayOfMonth)) {
        item.classList.add('selected');
        this._setSelectedDay(dayOfMonth);
      }
    }
  }

  private _handleTimesWrapperClick(event: Event) {
    const time = event.target as HTMLElement;
    if (!time.classList.contains('selected')) {
      this._getChildren('selectedTimeOption').forEach(selected => {
        selected.classList.remove('selected');
      });
      time.classList.add('selected');
      this._renderToggleContent();
    }
  }

  private _handlePrevMonthClick() {
    this._selectedDate = subMonths(this._selectedDate, 1);
    this._selectedDate = lastDayOfMonth(this._selectedDate);
    this._updateCalendar();
  }

  private _handleNextMonthClick() {
    this._selectedDate = addMonths(this._selectedDate, 1);
    this._selectedDate.setDate(1);
    this._updateCalendar();
  }

  private _setSelectedDay(day: number) {
    this._selectedDate = setDate(this._selectedDate, day);
    this._renderToggleContent();
    this._renderTimeIntervals();
  }

  public getSelection() {
    const selectedTimeOption = this._getChild('selectedTimeOption').innerHTML;
    const [periodAndFrom, to] = selectedTimeOption.split(' - ');
    const [_period, from] = periodAndFrom.split('</span>');
    return {
      from,
      to,
      date: this._selectedDate,
    };
  }

  public set settings({ interval, can_pickup_today_from, ...rest }: PickupSettings) {
    const start = startOfDay(interval.start);

    this._settings = {
      interval: {
        start: can_pickup_today_from ? start : addDays(start, 1),
        end: endOfDay(interval.end),
      },
      can_pickup_today_from,
      ...rest,
    };

    this._selectedDate = new Date(this._settings.interval.start);

    this._updateCalendar();

    this._getChild('calendar').classList.add('initialized');
    this._getChildComponent<StatefulButton>('stateful-button').isLoading = false;
  }

  protected _onEnter() {
    this._addListener('daysGrid', 'click', this._handleGridClick);
    this._addListener('prevMonth', 'click', this._handlePrevMonthClick);
    this._addListener('nextMonth', 'click', this._handleNextMonthClick);
    this._addListener('timesWrapper', 'click', this._handleTimesWrapperClick);
  }
}

export default Calendar;
